import { lazy, Suspense } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { AppShell } from "./components/AppShell";
import { useAuth } from "./components/AuthContext";
import { useVeniceAccount } from "./hooks/useVeniceAccount";
import { LandingPage, LoginPage, InvitePage } from "./pages/PublicPages";
import { ConnectVenicePage } from "./pages/ConnectVenicePage";
import { fullLocationPath } from "./lib/navigation";

const CatalogPage = lazy(() => import("./pages/CatalogPage").then((module) => ({ default: module.CatalogPage })));
const CampaignsPage = lazy(() => import("./pages/CampaignsPage").then((module) => ({ default: module.CampaignsPage })));
const CreatorPage = lazy(() => import("./pages/CreatorPage").then((module) => ({ default: module.CreatorPage })));
const NewCharacterDraftPage = lazy(() => import("./pages/CreatorPage").then((module) => ({ default: module.NewCharacterDraftPage })));
const NewCampaignDraftPage = lazy(() => import("./pages/CreatorPage").then((module) => ({ default: module.NewCampaignDraftPage })));
const CharacterEditorPage = lazy(() => import("./pages/CharacterEditorPage").then((module) => ({ default: module.CharacterEditorPage })));
const CampaignEditorPage = lazy(() => import("./pages/CampaignEditorPage").then((module) => ({ default: module.CampaignEditorPage })));
const SharedCharacterPage = lazy(() => import("./pages/SharedCreationPage").then((module) => ({ default: module.SharedCharacterPage })));
const SharedCampaignPage = lazy(() => import("./pages/SharedCreationPage").then((module) => ({ default: module.SharedCampaignPage })));
const CampaignPage = lazy(() => import("./pages/CampaignPage").then((module) => ({ default: module.CampaignPage })));
const LibrariesPage = lazy(() => import("./pages/LibrariesPage").then((module) => ({ default: module.LibrariesPage })));
const ProfilePage = lazy(() => import("./pages/ProfilePage").then((module) => ({ default: module.ProfilePage })));
const AdminPage = lazy(() => import("./pages/AdminPage").then((module) => ({ default: module.AdminPage })));
const DialectCoachPage = lazy(() => import("./pages/DialectCoachPage").then((module) => ({ default: module.DialectCoachPage })));

const suspense = (node: React.ReactNode) => <Suspense fallback={<div className="campaign-loader"><span className="boot-orbit"/><p>Opening view…</p></div>}>{node}</Suspense>;

function ProtectedShell() {
  const { session, loading } = useAuth();
  const location = useLocation();
  const venice = useVeniceAccount(Boolean(session));
  if (loading) return <div className="boot-screen"><span className="boot-orbit"/><p>Mapping your worlds…</p></div>;
  const from = fullLocationPath(location);
  if (!session) return <Navigate to="/login" replace state={{ from }} />;
  if (venice.isLoading) return <div className="boot-screen"><span className="boot-orbit"/><p>Checking your Venice account…</p></div>;
  if (!venice.data?.connected) return <Navigate to="/connect-venice" replace state={{ from }}/>;
  return <AppShell/>;
}

export function App() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage/>}/>
      <Route path="/login" element={<LoginPage/>}/>
      <Route path="/invite/:token" element={<InvitePage/>}/>
      <Route path="/connect-venice" element={<ConnectVenicePage/>}/>
      <Route path="/app" element={<ProtectedShell/>}>
        <Route index element={<Navigate to="catalog" replace/>}/>
        <Route path="catalog" element={suspense(<CatalogPage/>)}/>
        <Route path="campaigns" element={suspense(<CampaignsPage/>)}/>
        <Route path="create" element={suspense(<CreatorPage/>)}/>
        <Route path="create/character/new" element={suspense(<NewCharacterDraftPage/>)}/>
        <Route path="create/character/:characterId" element={suspense(<CharacterEditorPage/>)}/>
        <Route path="create/campaign/new" element={suspense(<NewCampaignDraftPage/>)}/>
        <Route path="create/campaign/:gameId" element={suspense(<CampaignEditorPage/>)}/>
        <Route path="shared/character/:id" element={suspense(<SharedCharacterPage/>)}/>
        <Route path="shared/campaign/:id" element={suspense(<SharedCampaignPage/>)}/>
        <Route path="libraries" element={suspense(<LibrariesPage/>)}/>
        <Route path="profile" element={suspense(<ProfilePage/>)}/>
        <Route path="admin" element={suspense(<AdminPage/>)}/>
        <Route path="dialect-coach" element={suspense(<DialectCoachPage/>)}/>
        <Route path="campaign/:campaignId" element={suspense(<CampaignPage/>)}/>
      </Route>
      <Route path="*" element={<Navigate to="/" replace/>}/>
    </Routes>
  );
}
